import { EmployeeGoalsFilter } from "./index";

/**
 * Migrates old filter format to new format for Employee Goals filters.
 *
 * **Why this migration is needed:**
 * - Employee Goals filter was updated to use new PY-Components (matching Company Goals pattern)
 * - The old filter format used objects with `{value, label}` structure: `{ status: [{ value: "active", label: "Active" }] }`
 * - The new format uses simple arrays: `{ status: ["active"] }`
 * - Existing URLs/bookmarks may contain old format filters in query params
 * - This migration ensures old URLs continue to work and are automatically updated to new format
 *
 * **Note:** Company Goals filter (GoalsList) doesn't need this migration because it was built fresh
 * with the new format from the start (PY-10081), so it never had old format filters in URLs.
 *
 * **What it does:**
 * - Converts old format filter objects to new format
 * - Handles all filter fields: status, transparency, assignees→assignee, category, outlook, etc.
 * - Both pages use query params (via useInfoPageTemplateQueryState), but only Employee Goals needs migration
 *
 * Old format: { status: [{ value: "active", label: "Active" }] }
 * New format: { status: ["active"] }
 */
export const migrateFiltersToNewFormat = (
	filters: Partial<EmployeeGoalsFilter> | Record<string, unknown>
): Partial<EmployeeGoalsFilter> => {
	if (!filters || typeof filters !== "object") {
		return {};
	}

	const migrated: Partial<EmployeeGoalsFilter> = { ...filters };

	// Migrate status: [{ value, label }] -> [value]
	if (migrated.status && Array.isArray(migrated.status)) {
		migrated.status = migrated.status.map((item: unknown) => {
			if (typeof item === "string") {
				return item;
			}
			if (typeof item === "object" && item !== null && "value" in item) {
				return (item as { value: string }).value;
			}
			return item;
		}) as string[];
	}

	// Migrate transparency: [{ value, label }] -> [value]
	if (migrated.transparency && Array.isArray(migrated.transparency)) {
		migrated.transparency = migrated.transparency.map((item: unknown) => {
			if (typeof item === "string") {
				return item;
			}
			if (typeof item === "object" && item !== null && "value" in item) {
				return (item as { value: string }).value;
			}
			return item;
		}) as string[];
	}

	// Migrate assignees -> assignee: [{ value, label }] -> [{ id, name }]
	if ("assignees" in migrated && migrated.assignees && Array.isArray(migrated.assignees)) {
		migrated.assignee = migrated.assignees.map((item: unknown) => {
			if (typeof item === "object" && item !== null) {
				if ("id" in item && "name" in item) {
					return item as { id: string; name: string };
				}
				if ("value" in item) {
					return {
						id: (item as { value: string }).value,
						name: (item as { label?: string; value: string }).label || (item as { value: string }).value,
					};
				}
			}
			return item;
		}) as { id: string; name: string }[];
		delete migrated.assignees;
	}

	// Migrate category: [{ value, label }] -> [{ id, name }]
	if (migrated.category && Array.isArray(migrated.category)) {
		migrated.category = migrated.category.map((item: unknown) => {
			if (typeof item === "object" && item !== null) {
				if ("id" in item && "name" in item) {
					return item as { id: string; name: string };
				}
				if ("value" in item) {
					return {
						id: (item as { value: string }).value,
						name: (item as { label?: string; value: string }).label || (item as { value: string }).value,
					};
				}
			}
			return item;
		}) as { id: string; name: string }[];
	}

	// Migrate outlook: [{ id, name, color }] or old format
	if (migrated.outlook && Array.isArray(migrated.outlook)) {
		migrated.outlook = migrated.outlook.map((item: unknown) => {
			if (typeof item === "object" && item !== null) {
				if ("id" in item && "name" in item && "color" in item) {
					return item as { id: string; name: string; color: string };
				}
				if ("id" in item) {
					return {
						id: (item as { id: string }).id,
						name: (item as { name?: string; id: string }).name || (item as { id: string }).id,
						color: (item as { color?: string; id: string }).color || "#222",
					};
				}
			}
			return item;
		}) as { id: string; name: string; color: string }[];
	}

	// Migrate showSharedOnly: [{ value, label }] -> [value]
	if (migrated.showSharedOnly && Array.isArray(migrated.showSharedOnly)) {
		migrated.showSharedOnly = migrated.showSharedOnly.map((item: unknown) => {
			if (typeof item === "string") {
				return item;
			}
			if (typeof item === "object" && item !== null && "value" in item) {
				return (item as { value: string }).value;
			}
			return item;
		}) as string[];
	}

	// Migrate unseenUpdatesOnly: [{ value, label }] -> [value]
	if (migrated.unseenUpdatesOnly && Array.isArray(migrated.unseenUpdatesOnly)) {
		migrated.unseenUpdatesOnly = migrated.unseenUpdatesOnly.map((item: unknown) => {
			if (typeof item === "string") {
				return item;
			}
			if (typeof item === "object" && item !== null && "value" in item) {
				return (item as { value: string }).value;
			}
			return item;
		}) as string[];
	}

	// Migrate alignedTo: { value, label, status, color } -> { id, name, status, color, ... }
	if (migrated.alignedTo && typeof migrated.alignedTo === "object" && migrated.alignedTo !== null) {
		const alignedTo = migrated.alignedTo as Record<string, unknown>;
		if ("value" in alignedTo && !("id" in alignedTo)) {
			migrated.alignedTo = {
				id: alignedTo.value as string,
				name: (alignedTo.label as string) || (alignedTo.value as string),
				status: (alignedTo.status as string) || "",
				color: (alignedTo.color as string) || "#222",
				assignee: "",
				created: "",
				due: "",
				target: 0,
				goalValue: 0,
				progress: 0,
				categories: [],
			} as EmployeeGoalsFilter["alignedTo"];
		}
	}

	return migrated;
};
